import { or } from 'tio-ui/utilities';
import { tracked } from '@glimmer/tracking';
import Component from '@glimmer/component';
import type { TOC } from '@ember/component/template-only';
import type Owner from '@ember/owner';
import { PhCaretDown } from 'ember-phosphor-icons';

interface VStackSignature {
  Args: {
    collapsed?: boolean;
  };
  Element: HTMLDivElement;
  Blocks: { default: [] };
}

export const VStack: TOC<VStackSignature> = <template>
  <div data-stack-collapsed={{if @collapsed "true" "false"}} data-vstack ...attributes>
    {{yield}}
  </div>
</template>;

interface HStackSignature {
  Args: {
    collapsed?: boolean;
  };
  Element: HTMLDivElement;
  Blocks: { default: [] };
}

export const HStack: TOC<HStackSignature> = <template>
  <div data-stack-collapsed={{if @collapsed "true" "false"}} data-hstack ...attributes>
    {{yield}}
  </div>
</template>;

interface SectionSignature {
  Args: {
    headerClass?: string;
    collapsed?: boolean;
    /**
     * Semantic level of the element wrapping the `:header` block (h1–h6).
     * Default: 2. The `:subHeader` renders one level below it. Pass text (not
     * a heading element) into `:header` — the wrapper IS the heading.
     */
    headingLevel?: 1 | 2 | 3 | 4 | 5 | 6;
    /**
     * Draw the section as a panel — the card hairline, radius and elevation,
     * plus the inner inset that belongs to a surface. Default is the flat
     * zone. Below 768px a panel is edge-to-edge either way, and a collapsed
     * stack overrides this: its children stay a flush list.
     */
    panel?: boolean;
    /** Flush record/table body with a separate, non-scrolling pagination footer. */
    layout?: 'list';
    /** Set false to omit a forwarded footer without reserving its inset. Defaults to true. */
    showFooter?: boolean;
  };
  Element: HTMLElement;
  Blocks: { default: []; header: []; subHeader: []; actions: []; body: []; footer?: [] };
}

/**
 * A titled region of a page: an optional header row (title, sub-header,
 * actions), a divider, and a body.
 *
 * Renders flat by default — a title and a rule, no surface — which is the
 * landing-page zone treatment, and takes no horizontal inset of its own, so
 * its title, its divider and the first card in its body line up with the page
 * gutter. `@panel={{true}}` opts one section into the panel surface.
 *
 * A shell carrying `data-section-panels` (tio-admin and tio-agent set it on
 * `<body>`) opts in a narrower set: sections beneath it that are direct
 * children of a **non-collapsed** `<VStack>` or `<HStack>` — exactly what the
 * old stack rule caught. A standalone section, or one nested inside a grid or
 * a plain wrapper, stays flat there and needs `@panel` to take the surface.
 *
 * Passing `@collapsed` turns the header row into a disclosure toggle. A
 * collapsed stack is a flush list, and that wins over `@panel` on its
 * children.
 */
export class Section extends Component<SectionSignature> {
  @tracked isOpen = true;

  constructor(owner: Owner, args: SectionSignature['Args']) {
    super(owner, args);
    if (args.collapsed) {
      this.isOpen = !args.collapsed;
    }
  }

  get isCollapsible() {
    return this.args.collapsed !== undefined;
  }

  get isClosed() {
    return !this.isOpen;
  }

  toggle = () => {
    this.isOpen = !this.isOpen;
  };

  get headingTag() {
    return `h${this.args.headingLevel ?? 2}` as const;
  }

  get subHeadingTag() {
    const level = this.args.headingLevel ?? 2;
    const sub = (level >= 6 ? 6 : level + 1) as 1 | 2 | 3 | 4 | 5 | 6;
    return `h${sub}` as const;
  }

  <template>
    <section
      data-section
      data-open={{if this.isOpen "true" "false"}}
      data-has-body={{if (has-block "body") "true" "false"}}
      data-panel={{if @panel "true"}}
      class={{if (eq @layout "list") "app-list-section"}}
      ...attributes
    >
      {{#if
        (or this.isCollapsible (has-block "header") (has-block "subHeader") (has-block "actions"))
      }}
        <div data-section-header-row class={{if (eq @layout "list") "app-list-section__header"}}>
          {{#if this.isCollapsible}}
            <button
              type="button"
              class="group h-6"
              {{on "click" this.toggle}}
              data-section-caret-trigger
            >
              <PhCaretDown
                class="w-4 h-4 tio-fg-info group-hover:tio-fg-info-body transition-transform duration-200 ease-out will-change-transform
                  {{if this.isOpen 'rotate-0' '-rotate-90'}}"
                data-section-caret
              />
            </button>
          {{/if}}
          <div data-section-header-inner>
            {{#if (or (has-block "header") (has-block "subHeader"))}}
              <div data-section-header-text>
                {{#if (has-block "header")}}
                  {{#let (element this.headingTag) as |HeadingTag|}}
                    <HeadingTag class={{@headerClass}} data-section-header>
                      {{yield to="header"}}
                    </HeadingTag>
                  {{/let}}
                {{/if}}
                {{#if (has-block "subHeader")}}
                  {{#if (has-block "header")}}
                    {{#let (element this.subHeadingTag) as |SubHeadingTag|}}
                      <SubHeadingTag data-section-sub-header>
                        {{yield to="subHeader"}}
                      </SubHeadingTag>
                    {{/let}}
                  {{else}}
                    {{#let (element this.headingTag) as |HeadingTag|}}
                      <HeadingTag data-section-sub-header>
                        {{yield to="subHeader"}}
                      </HeadingTag>
                    {{/let}}
                  {{/if}}
                {{/if}}
              </div>
            {{/if}}
            {{#if (has-block "actions")}}
              <div data-section-actions>
                {{yield to="actions"}}
              </div>
            {{/if}}
          </div>
        </div>
        {{#if this.isOpen}}
          <div data-section-divider></div>
        {{/if}}
      {{/if}}

      {{#if (has-block "body")}}
        <div
          hidden={{this.isClosed}}
          data-section-body-wrapper
          data-section-body
          class="app-section__body"
        >
          {{yield to="body"}}
        </div>
      {{/if}}
      {{yield}}
      {{#if (and (has-block "footer") (not (eq @showFooter false)))}}
        <div hidden={{this.isClosed}} class="app-section__footer">
          {{yield to="footer"}}
        </div>
      {{/if}}
    </section>
  </template>
}

export type { VStackSignature, HStackSignature, SectionSignature };
